import React, { Component } from "react";
import ReactTooltip from "react-tooltip";
import Layout from "layouts/DashboardManage";
import FolderPath from "components/Metadata/folder-path-designer";
import RenameFile from "components/Metadata/rename-file";
import { connect } from "react-redux";
import classNames from "classnames";
import PropTypes from "prop-types";
import uniqid from "uuid/v4";
import hasProperty from "lodash/has";
import AddFieldButton from "components/Forms/Buttons/AddField";
import * as inputTypes from "components/Forms/InputTypes";
import Notification, { notifyApiError } from "components/Notification";
import { SpinnerSyncBlock } from "components/Spinner";
import {
  getMachineLearningInfo,
  getMetadata,
  updateMetadata,
  addMachineLearningLabel
} from "store/actions/metadataAction";
import TextboxContainer from "./Inputs/TextboxContainer";
import MultiSelectContainer from "./Inputs/MultiSelectContainer";
import Textbox from "components/Forms/Elements/Textbox";
import SubmitButton from "components/Forms/Buttons/Submit";
import ErrorLabel from "components/Forms/Elements/ErrorLabel";
import Translate from "helpers/Translate";
import Preview from "./Preview";
import TextInputbox from "components/Forms/Elements/Textbox";
import SearchableDropDown from "components/Forms/Elements/SearchableDropDown";

export class Metadata extends Component {
  state = {
    title: "",
    machine_learning_label: "",
    client_label: "",
    client_approval_required: false,
    readonlyTitle: true,
    inputs: [],
    previewInputs: [],
    showDots: false,
    metadatadata: {}
  };
  componentDidMount() {
    this.props.getMachineLearningInfo();
    this.props.getMetadata(this.props.match.params.metadataId);
  }

  componentDidUpdate(prevProps) {
    // Check if the data has loaded
    if (
      prevProps.getMetadataResponse !== this.props.getMetadataResponse &&
      hasProperty(this, "props.getMetadataResponse.data")
    ) {
      const updatedInputs = this.props.getMetadataResponse.data.fields || [];
      const previewInputs = [...updatedInputs];
      this.setState({
        title: this.props.getMetadataResponse.data.title,
        machine_learning_label:
          this.props.getMetadataResponse.data.machine_learning_label,
        client_label: this.props.getMetadataResponse.data.client_label,
        client_approval_required:
          this.props.getMetadataResponse.data?.client_approval_required ||
          false,
        inputs: updatedInputs,
        previewInputs,
        showDots:
          this.props.getMetadataResponse.data.title.length > 23 ? true : false
      });
    }

    // When the new metadata is updated successfully, we will redirect manage page
    if (
      prevProps.updateMetadataResponse !== this.props.updateMetadataResponse &&
      hasProperty(this, "props.updateMetadataResponse.id")
    ) {
      return this.props.history.push(`/manage/metadata`);
    }

    // When there was a problem updating the metadata
    if (
      prevProps.errorMetadata !== this.props.errorMetadata &&
      this.props.errorMetadata &&
      Object.keys(this.props.errorMetadata).length > 0 &&
      hasProperty(this, "props.errorMetadata.response.data.data.errors")
    ) {
      let hasField = false;
      for (const error of this.props.errorMetadata.response.data.data.errors) {
        if (error.field === "fields") {
          hasField = true;
          break;
        }
      }

      if (hasField) {
        notifyApiError(this.props.errorMetadata);
      } else {
        Notification.error(
          Translate({ id: "an_error_occurred_submitting_the_form" })
        );
      }
    }
  }

  handleCreateElement = ({ inputType, insertAt = null, parent = null }) => {
    if (!inputType) return;

    const uuidSelf = uniqid();
    this.setState(prevState => {
      const element = {
        uuid_container: uuidSelf,
        parent,
        name: `sampleUniqueName${this.state.inputs.length + 1}`,
        machine_learning_label: "",
        client_label: "",
        visible: parent ? false : true,
        order: insertAt ? insertAt : prevState.inputs.length + 1,
        field_type: inputType,
        mandatory: false,
        rename: false,
        renaming_file_order: "",
        label: Translate({
          id: "Sample Label",
          values: [this.state.inputs.length + 1]
        }),
        value: (() => {
          switch (inputType) {
            case inputTypes.CHECKBOX:
            case inputTypes.MULTISELECT:
              return [];
            case inputTypes.DATERANGE:
              return ["", ""];
            default:
              return "";
          }
        })(),
        direction: inputTypes.HORIZONTAL,
        uuid_selected_options: [],
        default_selected_values: [],
        has_regex: false,
        regex: "",
        options: [
          {
            uuid_container: uuidSelf,
            uuid_child: uniqid(),
            dependencies: [],
            field_type: null,
            value: Translate({ id: "sample_value" })
          }
        ]
      };
      return { inputs: [...prevState.inputs, element] };
    });
    Notification.info(
      Translate({
        id: "a_new_field_has_been_added",
        values: [inputType.toUpperCase()]
      })
    );
    return uuidSelf;
  };

  handleInputChange = inputObject => {
    this.setState(prevState => {
      const newInputs = prevState.inputs.map(item => {
        if (item.uuid_container === inputObject.uuid_container) {
          // If the input has child options
          if (inputObject.hasChildOptions) {
            // If the changed input is a child textbox
            if (inputObject.isChildTextbox) {
              item.options.forEach((option, idx) => {
                if (option.uuid_child === inputObject.uuid_child) {
                  item.options[idx] = {
                    ...option,
                    value: inputObject.value
                  };
                }
              });
            } else {
              // Check if the changed item is part of radio, select or checkbox
              if (
                [inputTypes.RADIOBOX, inputTypes.SELECT].includes(
                  inputObject.type
                )
              ) {
                // to be deprecated
                item.uuid_selected_options = [];
                item.uuid_selected_options.push(inputObject.uuid_child);
                item.default_selected_values = [];
                item.default_selected_values.push(
                  ...inputObject.default_selected_values
                );
              } else if (
                [inputTypes.CHECKBOX, inputTypes.MULTISELECT].includes(
                  inputObject.type
                )
              ) {
                if (inputObject.isChecked) {
                  // to be deprecated
                  item.uuid_selected_options.push(inputObject.uuid_child);
                  item.default_selected_values = [
                    ...item.default_selected_values,
                    ...inputObject.default_selected_values
                  ];
                  delete inputObject.default_selected_values;
                } else {
                  // to be deprecated
                  item.uuid_selected_options =
                    item.uuid_selected_options.filter(
                      o => o !== inputObject.uuid_child
                    );
                  item.default_selected_values =
                    item.default_selected_values.filter(
                      o => !inputObject.default_selected_values.includes(o)
                    );
                  delete inputObject.default_selected_values;
                }
              }
            }
          }

          return {
            ...item,
            ...inputObject
          };
        } else {
          return item;
        }
      });

      return { inputs: newInputs };
    });
  };

  handleChange = e => {
    this.setState({
      [e.target.name]: e.target.value
    });
  };

  handleAddOption = uuidContainer => {
    this.setState(prevState => {
      const newInputs = prevState.inputs.map(item => {
        const newOptionObject = {
          uuid_container: uuidContainer,
          uuid_child: uniqid(),
          dependencies: [],
          value: `Sample Value`
        };

        if (item.uuid_container === uuidContainer) {
          return {
            ...item,
            options: [...item.options, newOptionObject]
          };
        } else {
          return item;
        }
      });

      return { inputs: newInputs };
    });
  };

  handleMoveClick = ({ input, direction }) => {
    const currentOrder = input.order;
    let targetOrder = currentOrder - 1;
    if (direction === "down") {
      targetOrder = currentOrder + 1;
    }

    this.setState(({ inputs }) => {
      inputs.forEach(item => {
        if (item.order === targetOrder) {
          item.order = currentOrder;
        } else if (item.uuid_container === input.uuid_container) {
          item.order = targetOrder;
        }
      });
      return inputs;
    });
  };

  handleRemoveOption = selectedItem => {
    this.setState(prevState => {
      const newInputs = prevState.inputs.map((item, idx) => {
        // Remove all dependent elements of the deleted child element
        if (item.parent && item.parent.uuid_child === selectedItem.uuid_child) {
          return false;
        }
        // Remove the child element
        if (item.uuid_container === selectedItem.uuid_container) {
          const filteredOptions = item.options.filter(
            o => o.uuid_child !== selectedItem.uuid_child
          );
          return {
            ...item,
            options: filteredOptions
          };
        } else {
          return item;
        }
      });

      return { inputs: newInputs };
    });
  };

  handleTrashClick = input => {
    let onOk = () => {
      this.setState(({ inputs }) => {
        const newInputs = inputs.filter(
          item => item.uuid_container !== input.uuid_container
        );

        newInputs
          .sort((a, b) => a.order - b.order)
          .forEach((item, idx) => {
            item.order = idx + 1;
          });

        return {
          inputs: newInputs
        };
      });
      Notification.info(Translate({ id: "item_has_been_removed" }));
    };
    let onCancel = () => {};

    Notification.confirm(Translate({ id: "are_you_sure" }), {
      labelOk: Translate({ id: "remove_field", uppercase: true }),
      onOk,
      onCancel
    });
  };

  handleUpdate = e => {
    e.preventDefault();
    this.state.inputs.forEach(inp => {
      switch (inp.field_type) {
        case inputTypes.CHECKBOX:
        case inputTypes.MULTISELECT:
          inp.value = [];
          break;
        case inputTypes.DATERANGE:
          inp.value = ["", ""];
          break;
        default:
          inp.value = inp.value;
      }
    });

    this.props.updateMetadata({
      id: this.props.match.params.metadataId,
      data: {
        title: this.state.title,
        machine_learning_label: this.state.machine_learning_label,
        client_label: this.state.client_label,
        client_approval_required: this.state.client_approval_required,
        fields: this.state.inputs,
        file: this.state.file,
        folder: this.state.folder
      }
    });
  };

  renderInputs = () => {
    return this.state.inputs
      .sort((a, b) => a.order - b.order)
      .map((input, idx) => {
        switch (input.field_type) {
          case inputTypes.TEXTBOX:
          case inputTypes.DATESOLO:
          case inputTypes.DATERANGE:
          case inputTypes.EMAIL:
          case inputTypes.NUMERIC:
          case inputTypes.TEXTAREA:
            return (
              <TextboxContainer
                key={idx}
                isFirstItem={idx === 0}
                isLastItem={idx + 1 === this.state.inputs.length}
                inputs={this.state.inputs}
                type={input.field_type}
                properties={{ key: idx, ...input }}
                errors={this.props.errorMetadata}
                onTrashClick={this.handleTrashClick}
                onMoveUpClick={() =>
                  this.handleMoveClick({
                    input,
                    direction: "up"
                  })
                }
                onMoveDownClick={() =>
                  this.handleMoveClick({
                    input,
                    direction: "down"
                  })
                }
                onInputChange={inputObj => this.handleInputChange(inputObj)}
              />
            );
          case inputTypes.MULTISELECT:
          case inputTypes.SELECT:
          case inputTypes.CHECKBOX:
          case inputTypes.RADIOBOX:
            return (
              <MultiSelectContainer
                key={idx}
                isFirstItem={idx === 0}
                isLastItem={idx + 1 === this.state.inputs.length}
                inputs={this.state.inputs}
                type={input.field_type}
                properties={{ key: idx, ...input }}
                errors={this.props.errorMetadata}
                onTrashClick={this.handleTrashClick}
                onMoveUpClick={() =>
                  this.handleMoveClick({
                    input,
                    direction: "up"
                  })
                }
                onMoveDownClick={() =>
                  this.handleMoveClick({
                    input,
                    direction: "down"
                  })
                }
                onInputChange={inputObj => this.handleInputChange(inputObj)}
                onNewOptionClick={uuid_container =>
                  this.handleAddOption(uuid_container)
                }
                onOptionRemove={uuid_container =>
                  this.handleRemoveOption(uuid_container)
                }
                onSelectMenu={({ inputType, parent }) => {
                  const newUUID = this.handleCreateElement({
                    inputType,
                    parent: { ...parent },
                    insertAt: input.order + 1
                  });

                  // Add dependencies to the parent that a child is dependent on it.
                  parent.dependencies = [...parent.dependencies, newUUID];
                }}
              />
            );
          default:
            return null;
        }
      });
  };
  onchildcomponentsupdate = (updatedState, statetype) => {
    if (statetype == "folder") {
      this.setState(prevState => ({
        ...prevState,
        folder: updatedState
      }));
    } else {
      this.setState(prevState => ({
        ...prevState,
        file: updatedState
      }));
    }
  };

  render() {
    return (
      <Layout>
        <ReactTooltip />
        {this.props.isLoading && <SpinnerSyncBlock />}
        <div className="meta-data">
          <h1 className="title mb-30">
            {Translate({ id: "add_edit_metadata" })}{" "}
          </h1>

          <div className="tabs">
            <div className="tab tab-manage selected1">
              {Translate({ id: "step_1_manage_meta_data" })}
            </div>
            <div className="tab tab-design selected">
              {Translate({ id: "step_2_input_field_designer" })}
            </div>
            <div className="tab tab-design selected">
              {Translate({ id: "step_3_folder_path_designer" })}
            </div>
            <div className="tab tab-design selected">
              {Translate({ id: "step_4_file_name_designer" })}
            </div>
          </div>

          <div className="tab-wrapper">
            <div className="tab-design-wrapper selected">
              {/* <!-- Design Panel --> */}
              <div className="design-pane">
                <div className="main-title">
                  <span className="mr-10">{Translate({ id: "title" })} :</span>
                  <i
                    className="fas fa-pencil-alt edit pointer mr-5"
                    onClick={() =>
                      this.setState({
                        readonlyTitle: !this.state.readonlyTitle
                      })
                    }
                  ></i>
                  <span>
                    <div
                      className="center"
                      data-tip={Translate({
                        id: "click_to_edit",
                        values: [this.state.title]
                      })}
                    >
                      <Textbox
                        type="text"
                        fieldValue={this.state.title}
                        name="title"
                        id="title"
                        placeholder={Translate({ id: "metadata_title" })}
                        classes={classNames("form-input", {
                          readonly: this.state.readonlyTitle
                        })}
                        onReturnKey={e => {
                          this.setState({
                            readonlyTitle: true
                          });
                        }}
                        onClick={() =>
                          this.setState({
                            readonlyTitle: false
                          })
                        }
                        onFocus={e => {
                          this.setState({
                            showDots: false
                          });
                        }}
                        onBlur={e => {
                          this.setState({
                            readonlyTitle: true,
                            showDots: e.target.value.length > 23 ? true : false
                          });
                        }}
                        errorKey={`title`}
                        errors={this.props.errorMetadata}
                        formErrorClass="form-error-inverse"
                        onChange={this.handleChange}
                      />
                      {this.state.showDots && "..."}
                    </div>
                  </span>
                </div>

                {/* Machine Learning Field */}
                <div className="mt-20">
                  <div className="fx-center-space-between mb-5">
                    <label htmlFor="machine_learning_label">
                      {Translate({ id: "machine_learning_document_type" })}:
                    </label>
                    <i
                      className="fas fa-info-circle mt-5"
                      data-tip={Translate({
                        id: "label_that_machine_learning_api_will_provide"
                      })}
                    ></i>
                  </div>

                  <SearchableDropDown
                    data={this.props.getAllMachineLearningTags}
                    defaultValue={this.state.machine_learning_label}
                    placeholder={Translate({
                      id: "select_ml_label"
                    })}
                    noResultLabel={Translate({
                      id: "add_ml_label"
                    })}
                    displayItemInList="label"
                    displayItemInTextbox="value"
                    onSelect={e => {
                      this.setState({
                        machine_learning_label: e.value
                      });
                      this.props.getMachineLearningInfo({ search: "" });
                    }}
                    onScroll={itemsCount =>
                      this.props.getMachineLearningInfo({ items: itemsCount })
                    }
                    keyword={keyword => {
                      this.props.getMachineLearningInfo({ search: keyword });
                      this.setState({
                        machine_learning_label: keyword
                      });
                    }}
                    onClose={() => {
                      this.props.getMachineLearningInfo({ search: "" });
                    }}
                    handleNoResult={mlTag => {
                      this.props.addMachineLearningLabel(mlTag);

                      Notification.info(
                        Translate({
                          id: "machine_learning_label_created_successfully"
                        })
                      );
                    }}
                    errorKey=""
                  />
                </div>

                {/* Client Field */}
                <div className="mt-20">
                  <div className="fx-center-space-between mb-5">
                    <label htmlFor="client_label">
                      {Translate({ id: "client_document_type" })}:
                    </label>
                    <i
                      className="fas fa-info-circle mt-5"
                      data-tip={Translate({
                        id: "label_that_client_api_will_provide"
                      })}
                    ></i>
                  </div>
                  <TextInputbox
                    id="client_label"
                    name="client_label"
                    placeholder={Translate({ id: "enter_client_label" })}
                    type="text"
                    errorKey={`client_label`}
                    fieldValue={this.state.client_label}
                    onReturnKey={this.handleChange}
                    onChange={this.handleChange}
                  />
                </div>

                {/* Client Approval Required */}
                <div className="mt-20">
                  <div className="fx-center-space-between mb-5">
                    <label htmlFor="client_approval_required">
                      <input
                        type="checkbox"
                        className="mr-10"
                        id="client_approval_required"
                        value={this.state.client_approval_required}
                        checked={this.state.client_approval_required}
                        onChange={e =>
                          this.setState({
                            client_approval_required: e.target.checked
                          })
                        }
                      />
                      {Translate({ id: "client_approval_required" })}
                    </label>
                    <i
                      className="fas fa-info-circle mt-5"
                      data-tip={Translate({
                        id: "if_this_checkbox_is_not_ticked_the_manager_has_the_option_of_uploading_reviewed_document_to_dms"
                      })}
                    ></i>
                  </div>
                  <ErrorLabel
                    formErrorClass="form-error-inverse"
                    errorKey="client_approval_required"
                    errors={this.props.errorMetadata}
                  />
                </div>

                {/* Add Field Button */}
                <AddFieldButton
                  onSelectMenu={({ inputType }) =>
                    this.handleCreateElement({ inputType })
                  }
                />

                <div className="clear"></div>
                {this.renderInputs()}
              </div>
              <div>
                {this.state.inputs.length > 0 ? (
                  <FolderPath
                    inputs={[...this.state.inputs]}
                    docTypeTitle={this.state.title}
                    onchildcomponentsupdate={this.onchildcomponentsupdate}
                  />
                ) : null}
              </div>
              <div>
                {this.state.inputs.length > 0 ? (
                  <RenameFile
                    inputs={[...this.state.inputs]}
                    docTypeTitle={this.state.title}
                    onchildcomponentsupdate={this.onchildcomponentsupdate}
                  />
                ) : null}
              </div>

              {/* <!-- Preview Panel --> */}
              <div className="preview-pane hide">
                <div className="title">{Translate({ id: "preview" })} </div>
                <div className="help">
                  {Translate({ id: "this_is_just_a_readonly_preview_section" })}
                </div>
                <Preview inputs={this.state.inputs} />
              </div>
            </div>

            {/* <!-- Save/Cancel Buttons --> */}
            <div className="save-cancel-buttons">
              {/* <!-- Cancel Button --> */}
              <button
                data-test="btn-cancel"
                onClick={() => this.props.history.push(`/manage/metadata`)}
                className="btn manage-btn-cancel"
              >
                <i className="far fa-times-circle mr-10"></i>
                {Translate({ id: "cancel" })}
              </button>

              {/* <!-- Save Button --> */}
              <SubmitButton
                className="btn btn-primary manage-btn-save"
                buttonIconClass="fas fa-save mr-10"
                buttonText={Translate({ id: "save_changes" })}
                btnId="create"
                data-test="btn-save"
                isLoading={this.props.isLoading}
                handleSubmit={this.handleUpdate}
              />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

Metadata.propTypes = {
  getMetadata: PropTypes.func.isRequired,
  updateMetadata: PropTypes.func.isRequired,
  metadata: PropTypes.object,
  isLoading: PropTypes.bool,
  getMetadataResponse: PropTypes.object,
  updateMetadataResponse: PropTypes.object,
  errorMetadata: PropTypes.object
};

const mapStateToProps = state => {
  return {
    getAllMachineLearningTags: state.metadata.getAllMachineLearningTags,
    getMetadataResponse: state.metadata.getMetadata,
    updateMetadataResponse: state.metadata.updateMetadata,
    errorMetadata: state.metadata.errorMetadata,
    isLoading: state.metadata.isLoadingMetadata
  };
};

export default connect(mapStateToProps, {
  getMachineLearningInfo,
  getMetadata,
  updateMetadata,
  addMachineLearningLabel
})(Metadata);
